// frontend/src/pages/groups/GroupDashboardPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import groupDashboardService from '../../services/groupDashboardService';
import { checkMembership } from '../../services/membershipService';
import Loader from '../../components/common/Loader';
import Button from '../../components/common/Button';

// Dashboard Components
import ExpenseManager from '../../components/dashboard/ExpenseManager';
import RoutePlanner from '../../components/dashboard/RoutePlanner';
import ActivityPlanner from '../../components/dashboard/ActivityPlanner';
import PollManager from '../../components/dashboard/PollManager';
import DashboardSummary from '../../components/dashboard/DashboardSummary';
import AccommodationManager from '../../components/dashboard/AccommodationManager';
import TransportationManager from '../../components/dashboard/TransportationManager';
import ChecklistManager from '../../components/dashboard/ChecklistManager';
import AIChatbot from '../../components/dashboard/AIChatbot';

// Icons
import {
  CurrencyRupeeIcon,
  MapIcon,
  CalendarIcon,
  ChartBarIcon,
  HomeIcon,
  TruckIcon,
  ClipboardDocumentCheckIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  ArrowLeftIcon,
  FireIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const GroupDashboardPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [membership, setMembership] = useState(null);
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showMobileStats, setShowMobileStats] = useState(false);
  
  // Check membership and load dashboard
  useEffect(() => {
    const loadDashboard = async () => {
      try {
        setLoading(true);
        
        // Check membership first
        const membershipData = await checkMembership(id);
        setMembership(membershipData);
        
        if (!membershipData.isCreator && !membershipData.isMember) {
          toast.error('You do not have access to this dashboard');
          navigate(`/groups/${id}`);
          return;
        }
        
        // Load dashboard summary
        const summary = await groupDashboardService.getDashboardSummary(id);
        setDashboardSummary(summary);
        
      } catch (err) {
        console.error('Dashboard load error:', err);
        setError('Failed to load dashboard');
        toast.error('Could not load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    
    loadDashboard();
  }, [id, navigate]);

  const handleViewProfile = (userId) => {
  // Navigate to user profile page
  navigate(`/user/${userId}`);
};
  
  // Navigation tabs - reordered for better mobile experience
  const tabs = [
    { id: 'summary', name: 'Dashboard', icon: <ChartBarIcon className="h-5 w-5" />, mobileIcon: <ChartBarIcon className="h-6 w-6" /> },
    { id: 'expenses', name: 'Expenses', icon: <CurrencyRupeeIcon className="h-5 w-5" />, mobileIcon: <CurrencyRupeeIcon className="h-6 w-6" /> },
    { id: 'routes', name: 'Routes', icon: <MapIcon className="h-5 w-5" />, mobileIcon: <MapIcon className="h-6 w-6" /> },
    { id: 'activities', name: 'Activities', icon: <CalendarIcon className="h-5 w-5" />, mobileIcon: <CalendarIcon className="h-6 w-6" /> },
    { id: 'polls', name: 'Polls', icon: <ChatBubbleLeftRightIcon className="h-5 w-5" />, mobileIcon: <ChatBubbleLeftRightIcon className="h-6 w-6" /> },
    { id: 'accommodation', name: 'Stay', icon: <HomeIcon className="h-5 w-5" />, mobileIcon: <HomeIcon className="h-6 w-6" /> },
    { id: 'transport', name: 'Transport', icon: <TruckIcon className="h-5 w-5" />, mobileIcon: <TruckIcon className="h-6 w-6" /> },
    { id: 'checklists', name: 'Checklists', icon: <ClipboardDocumentCheckIcon className="h-5 w-5" />, mobileIcon: <ClipboardDocumentCheckIcon className="h-6 w-6" /> },
    { id: 'members', name: 'Members', icon: <UserGroupIcon className="h-5 w-5" />, mobileIcon: <UserGroupIcon className="h-6 w-6" /> },
    { id: 'settings', name: 'Settings', icon: <Cog6ToothIcon className="h-5 w-5" />, mobileIcon: <Cog6ToothIcon className="h-6 w-6" /> }
    
  ];
  
  const activeTabName = tabs.find(tab => tab.id === activeTab)?.name || 'Dashboard';
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader size="lg" />
          <p className="mt-4 text-gray-600">Loading your travel dashboard...</p>
        </div>
      </div>
    );
  }
  
  if (error || !membership) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md p-6 bg-white rounded-2xl shadow-xl">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6 text-sm">
            {error || 'You need to be a member of this group to access the dashboard.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => navigate(`/groups/${id}`)}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-lg text-sm"
            >
              ← Back to Group
            </Button>
            <Button
              onClick={() => navigate('/groups')}
              className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg text-sm"
            >
              Browse Groups
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
     <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 pb-6">
      {/* Mobile Header - Sticky */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg sticky top-0 z-50 lg:relative">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-white/10 lg:hidden"
            >
              {mobileMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
            
            {/* Title */}
            <div className="flex-1 text-center lg:text-left ml-4 lg:ml-0">
              <h1 className="text-lg font-bold truncate">Travel Dashboard</h1>
              <p className="text-blue-100 text-xs hidden sm:block">Manage your trip efficiently</p>
            </div>
            
            {/* Back Button (Desktop) */}
            <button
              onClick={() => navigate(`/groups/${id}`)}
              className="p-2 rounded-full hover:bg-white/10 transition-colors hidden lg:block"
              title="Back to group"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            
            {/* User Badge */}
            <div className="hidden sm:block text-right">
              <p className="text-xs text-blue-200">Logged in as</p>
              <p className="text-sm font-medium truncate max-w-[120px]">{user?.name}</p>
            </div>
          </div>
          
          {/* Creator Badge - Mobile */}
          {membership.isCreator && (
            <div className="mt-2 flex items-center justify-center space-x-2 bg-yellow-500/20 px-3 py-1 rounded-full w-fit mx-auto sm:mx-0 sm:absolute sm:top-3 sm:right-4">
              <FireIcon className="h-4 w-4 text-yellow-300" />
              <span className="text-xs font-medium">Group Creator</span>
            </div>
          )}
        </div>
        
        {/* Mobile Stats Toggle */}
        {dashboardSummary && (
          <div className="px-4 pb-2">
            <button
              onClick={() => setShowMobileStats(!showMobileStats)}
              className="flex items-center justify-center w-full py-2 text-sm bg-white/10 rounded-lg"
            >
              <span>Quick Stats</span>
              {showMobileStats ? (
                <ChevronUpIcon className="h-4 w-4 ml-2" />
              ) : (
                <ChevronDownIcon className="h-4 w-4 ml-2" />
              )}
            </button>
          </div>
        )}
      </div>
      
      {/* Mobile Stats Dropdown */}
      {showMobileStats && dashboardSummary && (
        <div className="bg-white shadow-lg mx-4 mt-2 rounded-xl p-4 grid grid-cols-2 gap-3 animate-fadeIn">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-3 rounded-xl">
            <p className="text-xs opacity-90">Total Expenses</p>
            <p className="text-xl font-bold">₹{dashboardSummary.totalExpenses}</p>
          </div>
          
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-3 rounded-xl">
            <p className="text-xs opacity-90">Activities</p>
            <p className="text-xl font-bold">{dashboardSummary.totalActivities}</p>
          </div>
          
          <div className="bg-gradient-to-r from-purple-500 to-pink-600 text-white p-3 rounded-xl">
            <p className="text-xs opacity-90">Route Plans</p>
            <p className="text-xl font-bold">{dashboardSummary.totalRoutePlans}</p>
          </div>
          
          <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white p-3 rounded-xl">
            <p className="text-xs opacity-90">Members</p>
            <p className="text-xl font-bold">{dashboardSummary.membersCount}</p>
          </div>
        </div>
      )}
      
      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileMenuOpen(false)}>
          <div 
            className="absolute top-0 left-0 h-full w-64 bg-white shadow-xl animate-slideIn"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b">
              <h3 className="font-bold text-gray-800">Dashboard Menu</h3>
              <p className="text-sm text-gray-600">Select a section</p>
            </div>
            
            <div className="p-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setMobileMenuOpen(false);
                  }}
                  className={`flex items-center space-x-3 w-full p-3 rounded-lg mb-1 ${
                    activeTab === tab.id
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className={`${activeTab === tab.id ? 'text-blue-600' : 'text-gray-500'}`}>
                    {tab.mobileIcon}
                  </div>
                  <span className="font-medium">{tab.name}</span>
                  {activeTab === tab.id && (
                    <div className="ml-auto h-2 w-2 rounded-full bg-blue-600"></div>
                  )}
                </button>
              ))}
            </div>
            
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
              <div className="flex items-center space-x-3">
                <div className="h-8 w-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                  {user?.name?.charAt(0) || 'U'}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">{user?.name}</p>
                  <p className="text-xs text-gray-500">Dashboard</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-8">
        {/* Desktop Tab Navigation */}
        <div className="hidden lg:block mb-8">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-white text-blue-600 shadow-md'
                    : 'text-gray-600 hover:bg-white/50 hover:text-gray-800'
                }`}
              >
                {tab.icon}
                <span className="font-medium">{tab.name}</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Mobile Active Tab Indicator */}
        <div className="lg:hidden mb-6">
          <div className="flex items-center justify-between bg-white rounded-xl shadow p-4">
            <div>
              <h2 className="text-lg font-bold text-gray-800">{activeTabName}</h2>
              <p className="text-sm text-gray-600">Dashboard Section</p>
            </div>
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 rounded-lg bg-gray-100 text-gray-700"
            >
              <Bars3Icon className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        {/* Dashboard Content */}
        <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg lg:shadow-xl p-4 lg:p-6">
          {activeTab === 'summary' && (
            <DashboardSummary 
              groupId={id} 
              summary={dashboardSummary} 
              membership={membership} 
              onTabChange={setActiveTab}
              onViewProfile={handleViewProfile}  // Make sure this is passed
    navigate={navigate} 
            />
          )}
          
          {activeTab === 'expenses' && (
            <ExpenseManager 
              groupId={id} 
              membership={membership} 
              user={user} 
            />
          )}
          
          {activeTab === 'routes' && (
            <RoutePlanner 
              groupId={id} 
              membership={membership} 
              user={user} 
            />
          )}
          
          {activeTab === 'activities' && (
            <ActivityPlanner 
              groupId={id} 
              membership={membership} 
              user={user} 
            />
          )}
          
          {activeTab === 'polls' && (
            <PollManager 
              groupId={id} 
              membership={membership} 
              user={user} 
            />
          )}
          
          {activeTab === 'accommodation' && (
            <AccommodationManager 
              groupId={id} 
              membership={membership} 
              user={user} 
            />
          )}
          
          {activeTab === 'transport' && (
            <TransportationManager 
              groupId={id} 
              membership={membership} 
              user={user} 
            />
          )}
          
          {activeTab === 'checklists' && (
            <ChecklistManager 
              groupId={id} 
              membership={membership} 
              user={user} 
            />
          )}
          
          {activeTab === 'members' && (
            <div className="p-4 lg:p-8 text-center">
              <UserGroupIcon className="h-12 lg:h-16 w-12 lg:w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg lg:text-xl font-bold text-gray-800 mb-2">Members Management</h3>
              <p className="text-gray-600 mb-4 lg:mb-6 text-sm lg:text-base">
                Coming soon! Manage group members and permissions.
              </p>
              <Button
                onClick={() => navigate(`/groups/${id}`)}
                className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 lg:px-6 py-2 lg:py-3 rounded-lg text-sm lg:text-base"
              >
                View Group Members
              </Button>
            </div>
          )}
          
          {activeTab === 'settings' && (
            <div className="p-4 lg:p-8 text-center">
              <Cog6ToothIcon className="h-12 lg:h-16 w-12 lg:w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg lg:text-xl font-bold text-gray-800 mb-2">Group Settings</h3>
              <p className="text-gray-600 mb-4 lg:mb-6 text-sm lg:text-base">
                Dashboard settings and configurations coming soon!
              </p>
              {membership.isCreator && (
                <Button className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 lg:px-6 py-2 lg:py-3 rounded-lg text-sm lg:text-base">
                  Group Settings (Creator Only)
                </Button>
              )}
            </div>
          )}
        </div>
        
        {/* Desktop Quick Stats Footer */}
        {dashboardSummary && (
          <div className="hidden lg:grid mt-6 grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 rounded-xl shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Total Expenses</p>
                  <p className="text-2xl font-bold">₹{dashboardSummary.totalExpenses}</p>
                </div>
                <CurrencyRupeeIcon className="h-8 w-8 opacity-80" />
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-4 rounded-xl shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Activities</p>
                  <p className="text-2xl font-bold">{dashboardSummary.totalActivities}</p>
                </div>
                <CalendarIcon className="h-8 w-8 opacity-80" />
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-purple-500 to-pink-600 text-white p-4 rounded-xl shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Route Plans</p>
                  <p className="text-2xl font-bold">{dashboardSummary.totalRoutePlans}</p>
                </div>
                <MapIcon className="h-8 w-8 opacity-80" />
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white p-4 rounded-xl shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Members</p>
                  <p className="text-2xl font-bold">{dashboardSummary.membersCount}</p>
                </div>
                <UserGroupIcon className="h-8 w-8 opacity-80" />
              </div>
            </div>
          </div>
        )}
        
        {/* Mobile Bottom Navigation */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-2 z-40">
          <div className="flex justify-around">
            {tabs.slice(0, 4).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex flex-col items-center p-2 ${
                  activeTab === tab.id ? 'text-blue-600' : 'text-gray-500'
                }`}
              >
                <div className={`${activeTab === tab.id ? 'text-blue-600' : 'text-gray-400'}`}>
                  {tab.mobileIcon}
                </div>
                <span className="text-xs mt-1">{tab.name}</span>
                {activeTab === tab.id && (
                  <div className="h-1 w-1 rounded-full bg-blue-600 mt-1"></div>
                )}
              </button>
            ))}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="flex flex-col items-center p-2 text-gray-500"
            >
              <Bars3Icon className="h-6 w-6" />
              <span className="text-xs mt-1">More</span>
            </button>
          </div>
        </div>
      </div>
      
      {/* Add CSS animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slideIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        
        .animate-slideIn {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>
      <AIChatbot groupId={id} />
    </div>
  );
};

export default GroupDashboardPage;